import { ISlideModel, SlideModelSdkTypeV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    CopiedKeyType,
    CopyElementType,
    ICopiedNoteSlideData,
    ICopiedSdkData,
    ICopiedSlideData,
    ISdkData,
} from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import { IDrawingShapeData, IPresentationDataModel, IShapeData, ISlideData, ISlideDataModel, SlideDataModel } from '@tencent/slide-sdk-model';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import cloneDeep from 'lodash/cloneDeep';
import PasteSpecialUtils from '@src/slide/application/service/copy-paste/core/paste/paste-special/paste-special-utils';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import BasePasteSpecial from './paste-base-strategy';
import { IPasteSpecialServiceDelegates, IPasteSpecialStrategy } from './interface';
import { ElementSdkDataModify } from '@src/slide/application/service/copy-paste/core/paste/element-sdk-data-modify';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';

// 使用目标主题粘贴 ownData(默认行为)
export default class PasteDestinationTheme extends BasePasteSpecial implements IPasteSpecialStrategy {
    public constructor(copyInfo, services: IPasteSpecialServiceDelegates) {
        super(copyInfo, services);
    }

    public paste() {
        this.textSelectivePasteState?.beforePaste();
        // 先执行undo操作，撤销使用源格式粘贴
        this.undoRedoService?.undo();

        // 执行粘贴操作
        const pasteHandlerMap = {
            [CopyElementType.SHAPE]: this.pasteShape,
            [CopyElementType.SLIDE]: this.pasteSlide,
            [CopyElementType.TEXT]: this.pasteText,
        };
        pasteHandlerMap[this.type]?.call(this);

        // 数据上报
        this.report();
    }

    /**
     * 粘贴形状
     */
    private pasteShape(): void {
        const cloneDataList = cloneDeep(this.clipboardData.data);
        const ownSdkDataList = cloneDataList.map((allSdkData: ICopiedSdkData) => {
            let { ownSdkData } = allSdkData;
            // 处理占位符数据
            ownSdkData = PasteSpecialUtils.updatePlaceHolderData(
                ownSdkData as IShapeData,
                this.selectionService?.currentSlide?.sdkData as ISlideDataModel,
            ) as ISdkData;
            // 调整粘贴位置
            ElementSdkDataModify.adjustElementPosition(
                ownSdkData as IDrawingShapeData,
                this.selectionService?.currentSlide as ISlideModel,
                CopiedKeyType.OWN,
            );
            return ownSdkData;
        }) as IDrawingShapeData[];
        const api = (this.selectionService?.currentSlide?.sdkData as SlideModelSdkTypeV3)?.api;
        api?.addShapes(ownSdkDataList);
    }

    private pasteText() {
        // 需要等待撤回之后光标更新到正确位置再执行新的粘贴策略
        const off = this.textSelectionUndoRedo?.afterUndoRedo(() => {
            // eslint-disable-next-line max-len
            this.copyPasteManager?.dangerousInstance?.runTextSelectivePaste((this.clipboardData.data[0] as ICopiedSdkData<IShapeData>).ownSdkData);
            off?.();
            this.textSelectivePasteState?.afterPaste();
        });
    }

    /**
     * 粘贴幻灯片
     */
    private pasteSlide(): void {
        const uiDataService = this.selectionService?.uiDataService as IUiDataService;
        const sdkDataV3 = uiDataService.presentation.sdkData as IPresentationDataModel;
        const insertSlideIndex = this.insertSlideIndex as number;
        let rawSlideIdToSlideDataModelMap: Record<string, SlideDataModel> = {};
        sdkTransaction(() => {
            // 1、从目标文档中通过layoutName寻找目标版式，若找到，则修改layoutId为目标版式id
            // 2、若未找到，则创建新的版式，新版式的masterId更新为选中幻灯片的母版，修改ownData.layoutId = 新版式id
            const newSlideSdkDataList = PasteSpecialUtils.reorganizeSlideSdkData({
                clipboardData: this.clipboardData,
                insertSlideIndex: insertSlideIndex > 0 ? insertSlideIndex - 1 : insertSlideIndex,
                sdkDataV3,
                slideSdkDataModify: this.slideSdkDataModify,
                uiDataService,
            }) as ICopiedSlideData[];

            if (!newSlideSdkDataList) return {};
            const ownList: ISlideData[] = newSlideSdkDataList.map(allSdkData => allSdkData.ownSdkData);
            if (sdkDataV3?.sectionList?.length) {
                sdkDataV3?.api.addSlidesWithSection(ownList, this.insertGapIndex);
            } else {
                sdkDataV3?.api.addSlides(ownList, this.insertGapIndex);
            }

            // 建立原slideId与新slideDataModel的映射关系
            rawSlideIdToSlideDataModelMap = Object.fromEntries(newSlideSdkDataList
                .map(({ ownSdkData }: ICopiedSlideData, i: number) => [
                    ownSdkData.id,
                    sdkDataV3.slideList[insertSlideIndex + i] as SlideDataModel,
                ]));
            if (this.clipboardData.noteList) {
                cloneDeep(this.clipboardData.noteList).forEach((noteSlide: ICopiedNoteSlideData) => {
                    const slideDataModel = rawSlideIdToSlideDataModelMap?.[noteSlide.ownSdkData.slideId];
                    slideDataModel?.api.addNoteSlide({ ...noteSlide.ownSdkData, slideId: slideDataModel.id });
                });
            }
        });
    }

    /**
     * 上报粘贴使用目标主题
     */
    private report(): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PASTE_SPECIAL,
            action: TDWActionType.DESTINATION_THEME,
        });
    }
}
